/**
 * Reads a photographed menu and shows what was found, for review.
 *
 * The photo never reaches the model from here: it is posted to `/api/scan`,
 * which the dev server forwards to `scripts/scan-api.ts`. That is where the
 * DeepSeek key lives, and it is the one thing that must not be in the browser.
 *
 * Nothing is written until the merchant says so — a half-imported menu would be
 * worse than no import at all, and this runs mid-service.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { parseMoneyInput } from '@yummy/shared';
import {
  Button,
  Card,
  ErrorBanner,
  LoadingState,
  PageHeading,
  Row,
  Screen,
  Stack,
  Switch,
  Text,
  useLocale,
} from '@yummy/ui-rnr';
import {
  createCategory,
  createMenuItem,
  fetchMerchantMenu,
  setItemOptionGroups,
  type MerchantMenu,
} from '@yummy/db';

import { FooterBar } from '../components/FooterBar';
import { useMerchant } from '../app/auth';

import { supabase } from '../lib/supabase';
import { View } from 'react-native';

/**
 * Where the scan service is. In development that is the dev server's proxy to
 * `scripts/scan-api.ts`; everywhere else it is the deployed Edge Function, whose
 * URL is derived from the project URL rather than configured separately, so one
 * place knows where the project is.
 */
/**
 * A name two choices in one group share, or null.
 *
 * A repeated entry in a legend on the paper reads as two choices with the same
 * name, which the customer sees as two identical chips and the kitchen as the
 * same word twice. Worth flagging before the scan is imported.
 *
 * @param group A group as the model returned it.
 * @returns The repeated name, as it was written.
 */
function duplicateChoiceName(group: { choices?: { name: string }[] }): string | null {
  const seen = new Set<string>();
  for (const choice of group.choices ?? []) {
    const name = choice.name.trim();
    if (name === '') continue;
    const key = name.toLowerCase();
    if (seen.has(key)) return name;
    seen.add(key);
  }
  return null;
}

const SCAN_URL = import.meta.env.DEV
  ? '/api/scan'
  : `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scan-menu`;

interface ScannedItem {
  name: string;
  description?: string;
  price_text?: string;
  min_quantity?: number;
  groups?: {
    name: string;
    price_mode?: 'absolute' | 'delta';
    min_select?: number;
    max_select?: number;
    choices?: { name: string; price_text?: string }[];
  }[];
}

interface ScannedMenu {
  categories: { name: string; items: ScannedItem[] }[];
  /** Present on a refusal: how many were used, and the ceiling. */
  limit?: number;
  used?: number;
}

/**
 * Narrows a photo to something worth sending.
 *
 * A modern phone photo is 12MB, which base64 turns into 16MB of request for
 * text a 1600px image already reads cleanly.
 *
 * @param file The chosen photo.
 * @returns A JPEG data URL, no wider than 1600px.
 */
async function downscale(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1600 / bitmap.width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('could not read the image');
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.85);
}

/** Groups rows by their section, preserving the printed order. */
function groupBy<T extends { category: string }>(rows: T[]): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const list = groups.get(row.category) ?? [];
    list.push(row);
    groups.set(row.category, list);
  }
  return groups;
}

/** Opens the browser's picker and resolves with the file, if one is chosen. */
function pickPhoto(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
}

export function MenuScanScreen() {
  const { t } = useLocale();
  const navigate = useNavigate();
  // Cancel returns to the tab the scanner was opened from, not to All: a
  // merchant who abandons a scan has not moved on from what they were doing.
  const [searchParams] = useSearchParams();
  const returnCategory = searchParams.get('category');
  const back = useCallback(
    () => navigate(returnCategory ? `/menu?category=${returnCategory}` : '/menu'),
    [navigate, returnCategory],
  );
  const { restaurant } = useMerchant();
  const restaurantId = restaurant?.id ?? null;
  const [existing, setExisting] = useState<MerchantMenu | null>(null);
  const [skipped, setSkipped] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState<{ done: number; total: number } | null>(null);
  const [added, setAdded] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState<ScannedMenu | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);

  useEffect(() => {
    if (!restaurantId) return;
    void fetchMerchantMenu(supabase, restaurantId).then(setExisting).catch(() => setExisting(null));
  }, [restaurantId]);

  /** Whether this dish is already on the menu, which appending would duplicate. */
  const isDuplicate = (name: string) =>
    (existing?.items ?? []).some((i) => i.name.trim() === name.trim());

  /** Cents for printed text, or null when it cannot be read as money. */
  const cents = (text?: string) => {
    const value = text ? parseMoneyInput(text) : null;
    return value === null || Number.isNaN(value) ? null : value;
  };

  /** A dish priced by a group takes the cheapest of its choices. */
  const priceOf = (item: ScannedItem): number | null => {
    const groupPrices = (item.groups ?? [])
      .filter((group) => group.price_mode === 'absolute')
      .flatMap((group) => (group.choices ?? []).map((choice) => cents(choice.price_text)))
      .filter((value): value is number => value !== null);
    if (groupPrices.length) return Math.min(...groupPrices);
    return cents(item.price_text);
  };

  const rows = useMemo(() => {
    return (menu?.categories ?? []).flatMap((category) =>
      category.items.map((item) => ({
        category: category.name,
        item,
        // Excluded by default when the price cannot be read or the name is
        // already there: both would write something wrong, and the merchant
        // should choose that rather than discover it.
        needsAttention: priceOf(item) === null,
        duplicate: isDuplicate(item.name),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, existing]);

  const included = rows.filter(
    (row) => !skipped[`${row.category}\u0000${row.item.name}`] && !row.needsAttention && !row.duplicate
  );

  const scan = async () => {
    setError(null);
    const file = await pickPhoto();
    if (!file) return;

    setBusy(true);
    setMenu(null);
    try {
      const dataUrl = await downscale(file);
      setPhoto(dataUrl);

      const { data: session } = await supabase.auth.getSession();
      const response = await fetch(SCAN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'image/jpeg',
          Authorization: `Bearer ${session.session?.access_token ?? ''}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '',
          'x-restaurant-id': restaurantId ?? '',
        },
        body: await (await fetch(dataUrl)).blob(),
      });

      // A failed proxy answers with an empty body, so parsing first would throw
      // and hide the reason. Read the text, then decide what it was.
      const text = await response.text();
      let body: (ScannedMenu & { error?: string }) | null = null;
      try {
        body = text ? (JSON.parse(text) as ScannedMenu & { error?: string }) : null;
      } catch {
        body = null;
      }

      if (response.status === 429) {
        throw new Error(t('merchant.menu.scanLimited', { limit: body?.limit ?? 10 }));
      }
      if (!response.ok || !body) {
        throw new Error(body?.error ?? t('merchant.menu.scanUnreachable'));
      }
      setMenu(body);
      setSkipped({});
      setAdded(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Appends what is ticked, reusing categories that already exist.
   *
   * One RPC per dish rather than a bulk insert, so every write goes through the
   * same validation as hand entry. A 40-item menu takes a few seconds, which is
   * why it reports progress.
   */
  const addToMenu = async () => {
    if (!restaurantId || included.length === 0) return;
    setError(null);
    setAdded(null);
    setAdding({ done: 0, total: included.length });

    try {
      const byName = new Map((existing?.categories ?? []).map((c) => [c.name.trim(), c.id]));
      let done = 0;

      // The scan reads a menu top to bottom, and that order is part of what the
      // merchant photographed. It was thrown away here — every imported dish got
      // sort_order 0 — so a scanned menu arrived in whatever order the query
      // happened to tie-break, not the printed one.
      //
      // A category that already exists keeps its place and the scan appends to
      // it, so scanning a second sheet adds to the menu rather than reordering it.
      const lastOrder = new Map<string, number>();
      for (const item of existing?.items ?? []) {
        if (!item.category_id) continue;
        const current = lastOrder.get(item.category_id) ?? -1;
        lastOrder.set(item.category_id, Math.max(current, item.sort_order));
      }
      let nextCategoryOrder =
        (existing?.categories ?? []).reduce((max, c) => Math.max(max, c.sort_order), -1) + 1;

      for (const [categoryName, group] of groupBy(included)) {
        let categoryId = byName.get(categoryName.trim()) ?? null;
        if (!categoryId) {
          const created = await createCategory(supabase, restaurantId, {
            name: categoryName,
            sort_order: nextCategoryOrder++,
          });
          categoryId = created.id;
          byName.set(categoryName.trim(), categoryId);
        }

        let nextItemOrder = (lastOrder.get(categoryId) ?? -1) + 1;
        for (const row of group) {
          const price = priceOf(row.item) ?? 0;
          const created = await createMenuItem(supabase, restaurantId, {
            name: row.item.name,
            description: row.item.description ?? null,
            category_id: categoryId,
            price_cents: price,
            min_quantity: (row.item.min_quantity ?? 1) > 1 ? row.item.min_quantity : 1,
            sort_order: nextItemOrder++,
          });

          // The groups the menu printed — toppings, sugar and ice, swaps. The
          // model has been reading these since step four and this discarded
          // them: a merchant saw the choice on the review screen and it was gone
          // after saving, with nothing to say so.
          const groups = (row.item.groups ?? [])
            .map((group) => {
              const choices = (group.choices ?? [])
                .map((choice) => ({
                  name: choice.name,
                  price_cents: cents(choice.price_text) ?? 0,
                  is_active: true,
                }))
                .filter((choice) => choice.name.trim() !== '');
              const absolute = group.price_mode === 'absolute';
              return {
                name: group.name,
                price_mode: absolute ? ('absolute' as const) : ('delta' as const),
                // An absolute group is the dish's price, so exactly one choice
                // sets it; that is what the database requires.
                min_select: absolute ? 1 : Math.max(0, group.min_select ?? 0),
                max_select: absolute ? 1 : Math.max(1, group.max_select ?? choices.length),
                choices,
              };
            })
            .filter((group) => group.name.trim() !== '' && group.choices.length > 0);
          if (groups.length) await setItemOptionGroups(supabase, created.id, restaurantId, groups);

          done++;
          setAdding({ done, total: included.length });
        }
      }

      setAdded(done);
      const fresh = await fetchMerchantMenu(supabase, restaurantId);
      setExisting(fresh);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setAdding(null);
    }
  };

  const items = menu?.categories.reduce((n, c) => n + c.items.length, 0) ?? 0;

  return (
    <Screen
      footer={
        <FooterBar>
          <Row gap="sm" justify="end">
            {/* Only once there is something to add: before a scan this screen
                has nothing to put on the menu. */}
            {included.length > 0 ? (
              <Button
                onPress={addToMenu}
                disabled={adding !== null || included.length === 0}
                accessibilityLabel={t('merchant.menu.scanAdd', { count: included.length })}>
                <Text>
                  {adding
                    ? t('merchant.menu.scanAdding', { done: adding.done, total: adding.total })
                    : included.length === 0
                      ? t('merchant.menu.scanAddNone')
                      : t('merchant.menu.scanAdd', { count: included.length })}
                </Text>
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onPress={back}
              accessibilityLabel={t('common.cancel')}>
              <Text>{t('common.cancel')}</Text>
            </Button>
          </Row>
        </FooterBar>
      }>
      <Stack gap="lg">
        <PageHeading title={t('merchant.menu.scanTitle')} subtitle={t('merchant.menu.scanSubtitle')} />
        {error ? <ErrorBanner message={error} onRetry={() => setError(null)} /> : null}

        <Row gap="md" align="start" wrap>
          <Button onPress={scan} disabled={busy} accessibilityLabel={t('merchant.menu.scanChoose')}>
            <Text>{busy ? t('merchant.menu.scanReading') : t('merchant.menu.scanChoose')}</Text>
          </Button>
          {photo ? (
            // The photo beside the result, so a wrong price is a glance rather
            // than a hunt for the original.
            <img src={photo} alt="" style={{ height: 160, borderRadius: 8, border: '1px solid #e4e4e7' }} />
          ) : null}
        </Row>

        {busy ? <LoadingState message={t('merchant.menu.scanReading')} /> : null}

        {menu ? (
          <Stack gap="lg">
            <Text className="text-muted-foreground text-sm">
              {t('merchant.menu.scanFound', { categories: menu.categories.length, items })}
            </Text>

            {[...groupBy(rows)].map(([category, group]) => (
              <Card key={category} className="gap-2 p-4">
                <Text className="font-semibold">{category}</Text>
                {group.map((row) => {
                  const key = `${category}\u0000${row.item.name}`;
                  const on = !skipped[key] && !row.needsAttention && !row.duplicate;
                  return (
                    <Row key={key} spread gap="md" align="center">
                      <Stack gap="xs" className="min-w-0 shrink">
                        <Text className="text-sm">{row.item.name}</Text>
                        {row.item.description ? (
                          <Text className="text-subtle-foreground text-xs">{row.item.description}</Text>
                        ) : null}
                        {(row.item.groups ?? []).map((group) => {
                          const repeated = duplicateChoiceName(group);
                          return (
                            <View key={group.name}>
                              <Text className="text-subtle-foreground text-xs">
                                {group.min_select && group.min_select > 0 ? `${group.name} *` : group.name}
                                {' · '}
                                {(group.choices ?? [])
                                  .map((choice) =>
                                    choice.price_text ? `${choice.name} ${choice.price_text}` : choice.name
                                  )
                                  .join(' / ')}
                              </Text>
                              {repeated ? (
                                <Text className="text-warning text-xs">
                                  {t('merchant.menu.extrasDuplicate', { name: repeated })}
                                </Text>
                              ) : null}
                            </View>
                          );
                        })}
                        {/* Both of these would write something wrong, so they are
                            off until the merchant looks at them. */}
                        {row.duplicate ? (
                          <Text className="text-warning text-xs">
                            {t('merchant.menu.scanExists')}
                          </Text>
                        ) : null}
                        {row.needsAttention ? (
                          <Text className="text-warning text-xs">
                            {t('merchant.menu.scanNoPrice')}
                          </Text>
                        ) : null}
                      </Stack>
                      <Row gap="sm" align="center" className="shrink-0">
                        <Text className="text-sm font-semibold">
                          {row.item.price_text || '—'}
                          {(row.item.min_quantity ?? 1) > 1 ? ` · min ${row.item.min_quantity}` : ''}
                        </Text>
                        <Switch
                          checked={on}
                          onCheckedChange={() => setSkipped((prev) => ({ ...prev, [key]: on }))}
                          accessibilityLabel={row.item.name}
                        />
                      </Row>
                    </Row>
                  );
                })}
              </Card>
            ))}

            <Row gap="md" align="center" wrap>
              {added !== null ? (
                <Text className="text-success text-sm font-semibold">
                  {t('merchant.menu.scanAdded', { count: added })}
                </Text>
              ) : null}
            </Row>
          </Stack>
        ) : null}
      </Stack>
    </Screen>
  );
}
